import { useState, useMemo, useCallback, useEffect } from 'react';
import {
  useArCustomAging,
  useDownloadArAgingPdf,
} from '@/api/rpc/hooks/accounts-receivable/useArCustomAging';
import { useArAging } from '@/api/rpc/hooks/accounts-receivable/useArAging';
import { useArSyncStatus } from '@/api/rpc/hooks/accounts-receivable/useArSync';
import type {
  ArInvoiceFlatSortBy,
  GetArCustomAgingInput,
  InvoiceStatus,
} from '@erp/rpc';
import type { MultiSelectOption } from '@/components/common/MultiSelect';
import {
  type ArReportFilters,
  type Preset,
  DEFAULT_AR_REPORT_FILTERS,
  applyBasisLock,
  applyPreset,
  arAgingPayload,
  arCustomAgingPayload,
} from '@/components/accounts-receivable/ar-report-filters';
import { useArStatementBasisLock } from '@/api/rpc/hooks/accounts-receivable/useArStatementBasisLock';
import {
  type ViewMode,
  type CustomerOption,
  getDaysOut,
} from './ar-report-shared';

export type GroupedSortBy =
  'customerName' | 'openBalance' | 'invoiceCount' | 'lastStatementSentAt';

export function useArReportState() {
  // ── Filter state ──
  // A single object holds the basis + day-range cluster so that adding a new
  // filter field is purely a matter of extending ArReportFilters (see
  // ar-report-filters.ts). The hook never reaches into individual fields when
  // building RPC payloads — it routes everything through the typed builders.
  const [settings, setSettings] = useState<ArReportFilters>(
    DEFAULT_AR_REPORT_FILTERS,
  );
  const updateSettings = useCallback(
    (patch: Partial<ArReportFilters>) =>
      setSettings((prev) => ({ ...prev, ...patch })),
    [],
  );
  // Org-level basis lock. When enabled, `effectiveSettings` forces the
  // configured basis into every RPC payload and basis-derived sort; the panel
  // also hides the basis selector. No-op (same reference) when not locked.
  const basisLock = useArStatementBasisLock();
  const effectiveSettings = useMemo(
    () => applyBasisLock(settings, basisLock),
    [settings, basisLock],
  );
  const [selectedCustomers, setSelectedCustomers] = useState<CustomerOption[]>(
    [],
  );
  const [selectedStatuses, setSelectedStatuses] = useState<MultiSelectOption[]>(
    [],
  );
  const [activePreset, setActivePreset] = useState<string | null>(null);

  // ── "Last statement sent to customer" filter ──
  // minDays: only customers whose last send was at least N days ago
  // maxDays: only customers whose last send was at most N days ago
  // includeNeverSent: also include customers with no prior statement send
  const [lastSentMinDays, setLastSentMinDays] = useState<number | undefined>(
    undefined,
  );
  const [lastSentMaxDays, setLastSentMaxDays] = useState<number | undefined>(
    undefined,
  );
  const [includeNeverSent, setIncludeNeverSent] = useState(false);

  // ── UI state ──
  const [viewMode, setViewMode] = useState<ViewMode>('grouped');
  const [filterPanelOpen, setFilterPanelOpen] = useState(true);

  // ── Grouped view sort state ──
  const [groupedSortBy, setGroupedSortBy] =
    useState<GroupedSortBy>('openBalance');
  const [groupedSortDir, setGroupedSortDir] = useState<'asc' | 'desc'>('desc');

  // ── Flat view sort state ──
  const [flatSortBy, setFlatSortBy] =
    useState<ArInvoiceFlatSortBy>('balanceDue');
  const [flatSortDir, setFlatSortDir] = useState<'asc' | 'desc'>('desc');

  // ── Invoice detail slide-in ──
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string | null>(
    null,
  );

  // ── Bulk action state ──
  const [selectedCustomerIds, setSelectedCustomerIds] = useState<Set<string>>(
    new Set(),
  );
  const [bulkSendOpen, setBulkSendOpen] = useState(false);

  // ── External hooks ──
  const { lastSyncDisplay } = useArSyncStatus();
  const downloadPdfMutation = useDownloadArAgingPdf();

  // ── Derived: filters for the aging hook ──
  const filters: GetArCustomAgingInput = useMemo(
    () => ({
      ...arCustomAgingPayload(effectiveSettings),
      ...(selectedCustomers.length > 0 && {
        customerIds: selectedCustomers.map((c) => c.id),
      }),
      ...(selectedStatuses.length > 0 && {
        status: selectedStatuses.map((s) => s.id as InvoiceStatus),
      }),
      ...(lastSentMinDays !== undefined && {
        lastStatementSentMinDays: lastSentMinDays,
      }),
      ...(lastSentMaxDays !== undefined && {
        lastStatementSentMaxDays: lastSentMaxDays,
      }),
      ...(includeNeverSent && { includeNeverSentStatement: true }),
    }),
    [
      effectiveSettings,
      selectedCustomers,
      selectedStatuses,
      lastSentMinDays,
      lastSentMaxDays,
      includeNeverSent,
    ],
  );

  const { customers, totals, invoices, isLoading } = useArCustomAging(filters);

  const {
    customers: agingCustomers,
    totals: agingTotals,
    bucketDefinitions,
    isLoading: agingLoading,
  } = useArAging({
    ...arAgingPayload(effectiveSettings),
    ...(selectedCustomers.length > 0 && {
      customerIds: selectedCustomers.map((c) => c.id),
    }),
    ...(selectedStatuses.length > 0 && {
      status: selectedStatuses.map((s) => s.id as InvoiceStatus),
    }),
  });

  // ── Derived: sorted customers for grouped view ──

  const sortedCustomers = useMemo(() => {
    return [...customers].sort((a, b) => {
      const dir = groupedSortDir === 'asc' ? 1 : -1;
      switch (groupedSortBy) {
        case 'customerName':
          return (
            (a.customerName ?? '').localeCompare(b.customerName ?? '') * dir
          );
        case 'openBalance':
          return (
            (parseFloat(a.openBalance ?? '0') -
              parseFloat(b.openBalance ?? '0')) *
            dir
          );
        case 'invoiceCount':
          return (a.invoiceCount - b.invoiceCount) * dir;
        case 'lastStatementSentAt': {
          // Treat never-sent as "infinitely far in the past" so it sorts
          // oldest-first ascending and newest-first descending.
          const aTime = a.lastStatementSentAt
            ? Date.parse(a.lastStatementSentAt)
            : -Infinity;
          const bTime = b.lastStatementSentAt
            ? Date.parse(b.lastStatementSentAt)
            : -Infinity;
          if (aTime === bTime) return 0;
          return (aTime < bTime ? -1 : 1) * dir;
        }
        default:
          return 0;
      }
    });
  }, [customers, groupedSortBy, groupedSortDir]);

  // ── Bulk selection ──

  const toggleSelectAll = useCallback(() => {
    setSelectedCustomerIds((prev) => {
      const allIds = customers.map((c) => c.customerId);
      const allSelected =
        allIds.length > 0 && allIds.every((id) => prev.has(id));
      if (allSelected) return new Set();
      return new Set(allIds);
    });
  }, [customers]);

  const toggleSelectOne = useCallback((id: string) => {
    setSelectedCustomerIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const clearSelection = useCallback(
    () => setSelectedCustomerIds(new Set()),
    [],
  );

  // Clear selection when filters change. Depends on effectiveSettings (not raw
  // settings) so an org basis-lock change also resets the now-stale selection.
  useEffect(() => {
    setSelectedCustomerIds(new Set());
  }, [
    effectiveSettings,
    selectedCustomers,
    selectedStatuses,
    lastSentMinDays,
    lastSentMaxDays,
    includeNeverSent,
  ]);

  const someSelected = selectedCustomerIds.size > 0;
  const allSelected =
    customers.length > 0 &&
    customers.every((c) => selectedCustomerIds.has(c.customerId));

  const selectedCustomersForBulk = useMemo(
    () =>
      sortedCustomers
        .filter((c) => selectedCustomerIds.has(c.customerId))
        .map((c) => ({
          customerId: c.customerId,
          customerName: c.customerName,
          openBalance: c.openBalance,
          lastStatementSentAt: c.lastStatementSentAt,
        })),
    [sortedCustomers, selectedCustomerIds],
  );

  // ── Handlers ──

  function handlePresetClick(preset: Preset) {
    setSettings((prev) => applyPreset(prev, preset));
    setActivePreset(preset.label);
  }

  function handleSettingsChange(next: ArReportFilters) {
    setSettings(next);
    setActivePreset(null);
  }

  function handleMinDaysChange(e: React.ChangeEvent<HTMLInputElement>) {
    const val = parseInt(e.target.value, 10);
    updateSettings({ minDays: isNaN(val) ? 0 : Math.max(0, val) });
    setActivePreset(null);
  }

  function handleMaxDaysChange(e: React.ChangeEvent<HTMLInputElement>) {
    const trimmed = e.target.value.trim();
    if (trimmed === '') {
      updateSettings({ maxDays: undefined });
    } else {
      const val = parseInt(trimmed, 10);
      updateSettings({
        maxDays: isNaN(val) ? undefined : Math.max(0, val),
      });
    }
    setActivePreset(null);
  }

  function handleCustomerClick(
    customerId: string,
    customerName: string | null,
  ) {
    const existingOption: CustomerOption = {
      id: customerId,
      label: customerName ?? customerId,
    };
    setSelectedCustomers([existingOption]);
    setViewMode('flat');
  }

  function handleGroupedSortToggle(column: GroupedSortBy) {
    if (groupedSortBy === column) {
      setGroupedSortDir((prev) => (prev === 'desc' ? 'asc' : 'desc'));
    } else {
      setGroupedSortBy(column);
      setGroupedSortDir(column === 'customerName' ? 'asc' : 'desc');
    }
  }

  function handleFlatSortToggle(column: ArInvoiceFlatSortBy) {
    if (flatSortBy === column) {
      setFlatSortDir((prev) => (prev === 'desc' ? 'asc' : 'desc'));
    } else {
      setFlatSortBy(column);
      setFlatSortDir('desc');
    }
  }

  // ── Derived: sorted invoices ──

  const sortedInvoices = useMemo(() => {
    return [...invoices].sort((a, b) => {
      const dir = flatSortDir === 'asc' ? 1 : -1;
      switch (flatSortBy) {
        case 'netAmount': {
          const aVal = parseFloat(a.netAmount ?? '0');
          const bVal = parseFloat(b.netAmount ?? '0');
          return (aVal - bVal) * dir;
        }
        case 'appliedAmount': {
          const aVal =
            parseFloat(a.netAmount ?? '0') - parseFloat(a.balanceDue ?? '0');
          const bVal =
            parseFloat(b.netAmount ?? '0') - parseFloat(b.balanceDue ?? '0');
          return (aVal - bVal) * dir;
        }
        case 'balanceDue': {
          const aVal = parseFloat(a.balanceDue ?? '0');
          const bVal = parseFloat(b.balanceDue ?? '0');
          return (aVal - bVal) * dir;
        }
        case 'invoiceDate':
          return (a.invoiceDate ?? '').localeCompare(b.invoiceDate ?? '') * dir;
        case 'dueDate':
          return (a.dueDate ?? '').localeCompare(b.dueDate ?? '') * dir;
        case 'shipDate':
          return (a.shipDate ?? '').localeCompare(b.shipDate ?? '') * dir;
        case 'invoiceNumber':
          return (
            (a.invoiceNumber ?? '').localeCompare(b.invoiceNumber ?? '') * dir
          );
        case 'customerName':
          return (
            (a.customerName ?? '').localeCompare(b.customerName ?? '') * dir
          );
        case 'status':
          return (a.status ?? '').localeCompare(b.status ?? '') * dir;
        case 'firstSentAt': {
          // Treat unsent (null) as infinitely old — sorts first ascending,
          // last descending, same as the SQL ORDER BY in InvoiceService.
          if (!a.firstSentAt && !b.firstSentAt) return 0;
          if (!a.firstSentAt) return -1 * dir;
          if (!b.firstSentAt) return 1 * dir;
          return a.firstSentAt.localeCompare(b.firstSentAt) * dir;
        }
        case 'daysOut': {
          // NULLS LAST — invoices missing the basis date sort to the bottom
          // regardless of direction.
          const aDays = getDaysOut(a, effectiveSettings.basis);
          const bDays = getDaysOut(b, effectiveSettings.basis);
          if (aDays == null && bDays == null) return 0;
          if (aDays == null) return 1;
          if (bDays == null) return -1;
          return (aDays - bDays) * dir;
        }
        default:
          return 0;
      }
    });
  }, [invoices, flatSortBy, flatSortDir, effectiveSettings.basis]);

  // ── Derived: result count text ──

  const resultCountText = useMemo(() => {
    if (isLoading) return '';
    if (viewMode === 'flat') {
      return `Showing ${invoices.length} invoice${invoices.length !== 1 ? 's' : ''}`;
    }
    return `Showing ${customers.length} customer${customers.length !== 1 ? 's' : ''} (${totals?.invoiceCount ?? 0} invoices)`;
  }, [isLoading, viewMode, customers.length, totals, invoices.length]);

  return {
    // Filter state + setters
    settings,
    setSettings,
    updateSettings,
    // Basis-derived settings (org basis lock applied) + the lock flag, used by
    // the body to drive both the RPC payloads and the hidden basis selector.
    effectiveSettings,
    lockBasis: basisLock.lockBasis,
    selectedCustomers,
    setSelectedCustomers,
    selectedStatuses,
    setSelectedStatuses,
    activePreset,
    setActivePreset,
    lastSentMinDays,
    setLastSentMinDays,
    lastSentMaxDays,
    setLastSentMaxDays,
    includeNeverSent,
    setIncludeNeverSent,

    // UI state
    viewMode,
    setViewMode,
    filterPanelOpen,
    setFilterPanelOpen,

    // Sort
    groupedSortBy,
    groupedSortDir,
    handleGroupedSortToggle,
    flatSortBy,
    flatSortDir,
    handleFlatSortToggle,

    // Invoice detail
    selectedInvoiceId,
    setSelectedInvoiceId,

    // Bulk selection
    selectedCustomerIds,
    bulkSendOpen,
    setBulkSendOpen,
    toggleSelectAll,
    toggleSelectOne,
    clearSelection,
    someSelected,
    allSelected,
    selectedCustomersForBulk,

    // Data
    customers,
    sortedCustomers,
    totals,
    invoices,
    isLoading,
    filters,
    sortedInvoices,
    resultCountText,

    // Aging detail data
    agingCustomers,
    agingTotals,
    bucketDefinitions,
    agingLoading,

    // External hooks
    lastSyncDisplay,
    downloadPdfMutation,

    // Handlers
    handlePresetClick,
    handleSettingsChange,
    handleMinDaysChange,
    handleMaxDaysChange,
    handleCustomerClick,
  };
}

export type ArReportState = ReturnType<typeof useArReportState>;
